import React, { memo, useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { inject, observer } from "mobx-react";
import moment from "moment";
import { DATE_FORMAT_DEFAULT, TYPE_STATUS } from "../../utils/constants";
import {
    EmptyText,
    trimOverlengthString,
} from "../../components/common/CellText";
import TablePagination from "@mui/material/TablePagination";
import { useLocation, useNavigate } from "react-router-dom";
import queryString from "query-string";
import { getNameInCapitalize, TextWrap } from "../../utils/common";
import { StatusTag } from "../../components/common/StatusTag";
import SkeletonTable from "../../components/Skeleton/SkeletonTable";
import EmptyData from "../../components/image/EmptyData";
import UnreadText from "../../components/common/UnreadText";
import { MoreHorizCircle } from "iconoir-react";
import ContentModal from "../../components/dialog/ContentModal";

const checkUnread = (record) => record.status_internal_incoming !== "READ";
const renderAuthorityIssuedName = (name, isText = false) => {
    if (name.authority_issued_name) {
        return name.authority_issued_name;
    }
    if (name.other_authority_issued) {
        return (
            (isText && name.other_authority_issued) || (
                <span>{name.other_authority_issued}</span>
            )
        );
    }
    return "";
};

function createData(
    incoming_number,
    document_number,
    incoming_date,
    date_issued,
    document_status,
    title,
    record,
    handler,
    code
) {
    return {
        incoming_number,
        document_number,
        incoming_date,
        date_issued,
        document_status,
        title,
        record,
        handler,
        code,
    };
}

const InternalDocumentIncoming = ({ internalDocumentStore }) => {
    const {
        getInternalDocumentIncomingList,
        internalDocumentIncomingList,
        internalDocumentIncomingListTotalCount,
    } = internalDocumentStore;
    const location = useLocation();
    const navigate = useNavigate();
    const queryStringParse = queryString.parse(location.search, {
        arrayFormat: "index",
    });

    const [filter, setFilter] = useState({ page: 0, size: 10, title: null });
    const [searchString, setSearchString] = useState();
    const [isRefreshPage, setIsRefreshPage] = useState(true);
    const [loading, setLoading] = useState(true);
    const [openContent, setOpenContent] = useState(false);
    const [workSelect, setWorkSelect] = useState({});

    const NGAY_DEN_WIDTH = 100;
    const NGAY_VAN_BAN_WIDTH = 100;
    const SO_HIEU_WIDTH = 150;
    const TRANG_THAI_WIDTH = 120;
    const TRICH_YEU_WIDTH = 220;
    const CO_QUAN_BAN_HANH_WIDTH = 200;
    const LANH_DAO_WIDTH = 150;
    const SO_DEN_WIDTH = 60;

    useEffect(() => {
        const param = {
            page: queryStringParse.page || filter.page,
            size: queryStringParse.size || filter.size,
            title: queryStringParse.title || filter.title,
        };
        setFilter(param);
        setSearchString(queryStringParse.title || "");
        (async () => {
            setLoading(true);
            try {
                await Promise.all([getInternalDocumentIncomingList(param)]);
                setIsRefreshPage(false);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            internalDocumentStore.clearInternalDocumentIncomingList();
        };
    }, []);
    useEffect(() => {
        if (isRefreshPage) return;
        const queryStringStringify = queryString.stringify(filter, {
            skipNull: true,
        });
        navigate({
            pathname: "/internal-document/incoming-document",
            search: queryStringStringify,
        });
        (async () => {
            setLoading(true);
            try {
                await getInternalDocumentIncomingList(filter);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        })();
    }, [filter]);
    const handleQuickSearch = () => {
        setFilter({ ...filter, page: 0, title: searchString });
    };

    const handleChangeRowsPerPage = (event) => {
        setFilter({
            ...filter,
            page: 0,
            size: parseInt(event.target.value, 10),
        });
    };
    const onChangePagination = (event, pageIndex) => {
        setFilter({ ...filter, page: +pageIndex });
    };
    const rows = internalDocumentIncomingList.map((record) =>
        createData(
            <UnreadText
                text={record.incoming_number}
                isUnread={checkUnread(record)}
                width={SO_DEN_WIDTH}
            />,
            <UnreadText
                text={record.document_number}
                isUnread={checkUnread(record)}
                width={SO_HIEU_WIDTH}
            />,
            <UnreadText
                text={moment(record.incoming_date).format(DATE_FORMAT_DEFAULT)}
                isUnread={checkUnread(record)}
                width={NGAY_DEN_WIDTH}
            />,
            record.date_issued ? (
                <UnreadText
                    text={moment(record.date_issued).format(
                        DATE_FORMAT_DEFAULT
                    )}
                    isUnread={checkUnread(record)}
                    width={NGAY_VAN_BAN_WIDTH}
                />
            ) : (
                <TextWrap>
                    <EmptyText>-----------------</EmptyText>
                </TextWrap>
            ),
            StatusTag(
                TYPE_STATUS.INTERNAL_DOCUMENT_INCOMING,
                record.document_status
            ),
            //     <UnreadText
            //     text={record?.title}
            //     isUnread={checkUnread(record)}
            //     width={CONG_VIEC_WIDTH - 20}
            //
            // />,
            <UnreadText
                text={record.title}
                isUnread={checkUnread(record)}
                width={TRICH_YEU_WIDTH}
                icon={
                    <MoreHorizCircle
                        className="text-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenContent(true);
                            setWorkSelect(record);
                        }}
                    />
                }
            />,
            <UnreadText
                text={renderAuthorityIssuedName(record)}
                isUnread={checkUnread(record)}
                width={CO_QUAN_BAN_HANH_WIDTH}
            />,
            record.handler ? (
                <UnreadText
                    text={getNameInCapitalize(record.handler)}
                    isUnread={checkUnread(record)}
                    width={LANH_DAO_WIDTH}
                />
            ) : (
                <TextWrap>
                    <EmptyText>-----------------</EmptyText>
                </TextWrap>
            ),
            record.code
        )
    );

    return (
        <>
            <input
                type="text"
                placeholder="Tìm kiếm"
                className="bg-[#F8F8F8] mt-2 px-5 py-4 rounded-xl w-full  focus-visible:outline-none"
                onChange={(e) => {
                    setSearchString(e.target.value);
                }}
                value={searchString}
                onSubmit={handleQuickSearch}
                onBlur={handleQuickSearch}
                onKeyPress={(e) => {
                    if (e.key === "Enter") {
                        handleQuickSearch();
                    }
                }}
            />
            {loading ? (
                <SkeletonTable />
            ) : internalDocumentIncomingList?.length > 0 ? (
                <>
                    <section className="mt-5 overflow-x-auto hideScrollbar">
                        <TableContainer
                            component={Paper}
                            className="inline-block w-auto overflow-visible hideScrollbar shadow-none border-[1px] border-[rgba(224,224,224,1)] rounded-lg"
                        >
                            <Table
                                sx={{ width: 1350 }}
                                aria-label="simple table"
                                className=""
                            >
                                <TableHead className=" bg-primary">
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                width: SO_DEN_WIDTH,
                                            }}
                                            className="font-bold text-white rounded-tl-lg "
                                        >
                                            Số đến
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: SO_HIEU_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Số hiệu
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: NGAY_DEN_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Ngày đến
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: NGAY_VAN_BAN_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Ngày văn bản
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: TRANG_THAI_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Trạng thái
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: TRICH_YEU_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Trích yếu
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: CO_QUAN_BAN_HANH_WIDTH,
                                            }}
                                            className="font-bold text-white "
                                        >
                                            Cơ quan ban hành
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: LANH_DAO_WIDTH,
                                            }}
                                            className="font-bold text-white rounded-tr-lg "
                                        >
                                            Lãnh đạo xử lý
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow
                                            onClick={() => {
                                                navigate(
                                                    `/internal-document/incoming-document/view/${row.code}`
                                                );
                                            }}
                                            key={row.code}
                                            sx={{
                                                "&:last-child td, &:last-child th":
                                                    {
                                                        border: 0,
                                                    },
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                scope="row"
                                            >
                                                {row.incoming_number}
                                            </TableCell>
                                            <TableCell>
                                                {row.document_number}
                                            </TableCell>
                                            <TableCell>
                                                {row.incoming_date}
                                            </TableCell>
                                            <TableCell>
                                                {row.date_issued}
                                            </TableCell>
                                            <TableCell>
                                                {row.document_status}
                                            </TableCell>
                                            <TableCell>{row.title}</TableCell>
                                            <TableCell>{row.record}</TableCell>
                                            <TableCell>{row.handler}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </section>
                    <TablePagination
                        component="div"
                        count={internalDocumentIncomingListTotalCount}
                        page={+filter.page}
                        onPageChange={onChangePagination}
                        rowsPerPage={filter.size}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Số bản ghi"
                    />
                </>
            ) : (
                <EmptyData />
            )}
            <ContentModal
                open={openContent}
                handleClose={() => {
                    setOpenContent(false);
                }}
                content={workSelect?.title}
                headerTitle="Văn bản đến"
            />
        </>
    );
};

export default memo(
    inject("internalDocumentStore")(observer(InternalDocumentIncoming))
);
